<template>
  <b-dropdown
    split
    :split-class="buttonClasses"
    :toggle-class="buttonClasses"
    class="split-button"
    :class="{ 'disabled-split-button': !canExport }"
    @click.prevent="beginExport(TranslationFileType.Excel)"
    :disabled="!canExport"
    v-tooltip="$t(props.translationGroup ? 'translationManager.exportTable' : 'translationManager.exportProject')">
    <template #button-content>
      <Icon icon="Export" /><span v-if="!props.iconOnly">{{
        $t(props.translationGroup ? "translationManager.exportTable" : "translationManager.exportProject")
      }}</span>
    </template>
    <b-dropdown-item link-class="icon-item" @click.prevent="beginExport(TranslationFileType.Excel)">
      <div class="dd-item-container">
        <span class="dd-item-icon"><Icon icon="FileExcel"></Icon></span>
        <span class="dd-item-text">{{ $t("translationManager.exportExcel") }}</span>
      </div>
    </b-dropdown-item>
    <b-dropdown-item link-class="icon-item" @click.prevent="beginExport(TranslationFileType.XLIFF)">
      <div class="dd-item-container">
        <span class="dd-item-icon"><Icon icon="Export"></Icon></span>
        <span class="dd-item-text">{{ $t("translationManager.exportXliff") }}</span>
      </div>
    </b-dropdown-item>
  </b-dropdown>
  <ProgressModal :progress="vm.progressHandler.progress" @cancel="exportCancelled" :min-open-time="1000" :auto-close="true"></ProgressModal>
</template>

<script setup lang="ts">
import Icon from "@common/components/Icon.vue";
import ProgressModal from "@common/components/ProgressModal.vue";
import { useGlobalParameters } from "@common/composable/useRootStore";
import { ActivityCriteria } from "@medapro-types/common/activity/activity";
import { ActivityAction, ActivityObject, ActivityProcess, ActivitySeverity, ActivityStatus } from "@medapro-types/common/activity/activity-enums";
import { ProgressHandler } from "@medapro-types/common/components/ProgressHandling";
import { TranslationFileType, TranslationGroupType } from "@medapro-types/TranslationManager/translationManager";
import activityApi from "@services/activity";
import applications from "@services/applications";
import { useTranslationManagerActions } from "@TranslationManager/composables/useTranslationManagerStore";
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";

//#region General
const props = withDefaults(
  defineProps<{
    id: string;
    translationProjectId?: string | null;
    translationGroup?: TranslationGroupType | null;
    translationProjectName?: string | null;
    dataChangeAllowedCheck: () => boolean;
    disabled?: boolean;
    iconOnly?: boolean;
  }>(),
  {
    disabled: false,
    iconOnly: true
  }
);
//#endregion

//#region Store
const { exportTranslations } = useTranslationManagerActions();
const { globalParameters } = useGlobalParameters();
const { t: $t } = useI18n();
//#endregion

//#region ViewModel
type ViewModel = {
  progressHandler: ProgressHandler;
};

const vm = reactive<ViewModel>({
  progressHandler: ProgressHandler.createIndeterminate({ id: `${props.id}-button-progress-modal`, isCancellable: true })
});
//#endregion

//#region Export
const exportCancelled = () => {
  vm.progressHandler.cancel(`${$t("translationManager.cancellingExport")}...`);
};

const beginExport = async (fileType: TranslationFileType) => {
  if (!canExport.value && !props.dataChangeAllowedCheck()) {
    return;
  }

  const result = await exportTranslations({
    fileType: fileType,
    translationGroup: props.translationGroup,
    progressHandler: vm.progressHandler
  });

  if (result) {
    await createActivityEntry(result.filename);
  }
};

const createActivityEntry = async (filename: string) => {
  try {
    if (globalParameters.value.IsActivityFeedEnabled) {
      const activityCriterias: ActivityCriteria[] = [
        {
          severity: ActivitySeverity.Information,
          process: ActivityProcess.Export,
          objectType: ActivityObject.TranslationProject,
          status: ActivityStatus.Successful,
          action: ActivityAction.Completed,
          descriptionValues: [filename],
          descriptionKey: "TranslationProjectExported",
          objectValue: props.translationProjectName,
          applicationId: applications.Modules.TranslationManager.Id
        }
      ];

      await activityApi.createActivity(activityCriterias);
    }
  } catch (error) {
    console.error("Activity insertion for translation project export failed with error : ", error);
  }
};

const canExport = computed(() => {
  return props.translationProjectId && !props.disabled;
});

const buttonClasses = computed(() => {
  return `mono ${props.iconOnly ? "btn-icon" : "btn-with-icon"}`;
});
//#endregion
</script>
